import React, { useEffect, useState } from 'react';
import { Form, Grid, Input } from 'antd';
import qs from 'qs';
import { useLocation } from 'react-router';
import { StyledButtonLogin } from 'components/styledComponents/button/Button.styled';
import { AppConstants } from 'constants/index';
import { FormInputWrapper } from 'components/styledComponents/input/Input.styled';
import { StyledFormMainWrapper } from 'components/styledComponents/form/Form.styled';
import { NavigationService } from 'services';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectCallEndedStatus } from 'app/settings/selectors';
import { changeEndedStatus } from 'app/settings/slices';
import { StyledAuthMainWrapper, StyledTitleForm } from 'app/auth/components/Login.styled';
import { FormItemMarginTopWrapper } from 'shared/Common.styled';
import withDefaultProps from 'shared/withDefaultProps';
import { LabelInfo } from 'shared/ui';
import { FormItemErrorMessage } from 'shared/formControl';

interface ModifyPasswordProps {
  send: (params: { data: Record<string, unknown>; text: string }) => void;
  title: string;
}

const ModifyPassword = ({ send, title }: ModifyPasswordProps) => {
  const location = useLocation();

  const [form] = Form.useForm();
  const { validateFields, getFieldValue } = form;

  const [confirmDirty, setConfirmDirty] = useState(false);

  const endedStatus = useAppSelector(selectCallEndedStatus);

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (endedStatus) {
      dispatch(changeEndedStatus?.(false));
      NavigationService('/login');
    }
  }, [dispatch, endedStatus]);

  const inputs = {
    newPassword: 'newPassword',
    confirmPassword: 'confirmPassword',
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const { code } = qs.parse(location.search, { ignoreQueryPrefix: true });
    validateFields().then((values) => {
      send({ data: { ...values, code }, text: title.toLowerCase() });
    });
  };

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const handleConfirmBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setConfirmDirty(confirmDirty || !!value);
  };

  const newPasswordInfo = 'Only passwords with minimum of 8 characters including at least 1 number and 1 uppercase letter are accepted.';

  return (
    <StyledAuthMainWrapper md={screens.md ? 1 : 0} xs={screens.xs ? 1 : 0}>
      <StyledFormMainWrapper layout="vertical" form={form}>
        <StyledTitleForm>{`${title} Password`}</StyledTitleForm>
        <FormInputWrapper $hasLabelInfo>
          <Form.Item
            label={<LabelInfo label="New Password" info={newPasswordInfo} />}
            name={inputs.newPassword}
            rules={[
              {
                required: true,
                message: <FormItemErrorMessage errorMassage="New password cannot be empty!" />,
              },
              {
                pattern: new RegExp(AppConstants.regex.password),
                message: <FormItemErrorMessage errorMassage="Password can be 8-40 characters long, including at least a number and uppercase letter." />,
              },
            ]}
          >
            <Input.Password type="password" placeholder="New Password" />
          </Form.Item>
        </FormInputWrapper>
        <FormItemMarginTopWrapper>
          <FormInputWrapper>
            <Form.Item
              label="Confirm Password"
              name={inputs.confirmPassword}
              dependencies={[inputs.newPassword]}
              rules={[
                {
                  required: true,
                  message: <FormItemErrorMessage errorMassage="Confirm password cannot be empty!" />,
                },
                () => ({
                  validator(rule, value) {
                    if (!value || getFieldValue(inputs.newPassword) === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(<FormItemErrorMessage errorMassage="Passwords do not match!" />);
                  },
                }),
              ]}
            >
              <Input.Password type="password" onBlur={handleConfirmBlur} placeholder="Confirm Password" />
            </Form.Item>
          </FormInputWrapper>
        </FormItemMarginTopWrapper>
        <StyledButtonLogin $isMarginTop type="primary" htmlType="submit" onClick={handleSubmit}>
          Save
        </StyledButtonLogin>
      </StyledFormMainWrapper>
    </StyledAuthMainWrapper>
  );
};

export default withDefaultProps(ModifyPassword, {});
